import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { PropertyUnitsService } from 'src/app/features/property-units/services/property-units.service';
import { AyenServices } from 'src/app/core/services/ayen-services/ayen-services.service';
import { ClientsService } from 'src/app/features/customers/services/clients.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddClientComponent } from 'src/app/shared/components/add-client/add-client.component';
import { DynamicServices } from 'src/app/features/ayen-services/services/dynamic-services/dynamic-services.service';
import { OrderDetailsService } from '../../services/order-details/order-details.service';
import { AdminOrdersService } from '../../services/orders/admin-orders.service';

@Component({
  selector: 'property-building-details',
  standalone: false,
  templateUrl: './property-building-details.component.html',
  styleUrls: ['./property-building-details.component.scss'],
})
export class PropertyBuildingDetailsComponent implements OnInit {
  @Input() orderId: any;
  @Input() orderData: any;
  @Output() errorMsg = new EventEmitter();
  @Output() building_view = new EventEmitter();

  form: FormGroup;
  spinner: boolean = false;
  alertmsg: boolean = false;
  errorMessage: string = '';
  
  // View switching for add unit
  currentView: 'main' | 'add-unit' = 'main';
  pendingNewUnitId: number | null = null;
  pendingPackageId: number | null = null;
  
  // Store order data when loading existing order
  order: any;

  // Services dropdown data
  services: any[] = [];
  servicesList: any[] = [];
  servicesPageCount: number = 0;
  selectedService: any;

  // Customers dropdown data
  clients: any[] = [];
  clientsList: any[] = [];
  clientsPageCount: number = 0;
  selectedClient: any;

  // Customer's units (loaded after service + customer selection)
  customerUnits: any[] = [];
  showUnitsDropdown: boolean = false;

  // Available packages (loaded after unit selection)
  availablePackages: any[] = [];
  showPackagesDropdown: boolean = false;

  public clientDialogRef!: MatDialogRef<AddClientComponent>;

  constructor(
    private fb: FormBuilder,
    private clientsService: ClientsService,
    private dialog: MatDialog,
    private dynamicServices: DynamicServices,
    private router: Router,
    private route: ActivatedRoute,
    private propertyUnitsService: PropertyUnitsService,
    private adminOrdersService: AdminOrdersService,
  ) {
    this.form = this.fb.group({
      service_id: [null, Validators.required],
      customer_id: [null, Validators.required],
      property_unit_id: [null],
      package_id: [null],
    });
  }

  ngOnInit(): void {
    this.getDynamicServices({ params: { page: 1 }, clearList: true });
    this.getDynamicClients({ params: { page: 1 }, clearList: true });
    this.setupFormListeners();
    
    // Check for order ID in query params
    this.route.queryParams.subscribe((params) => {
      if (params['id']) {
        this.orderId = +params['id'];
        if (this.orderId) {
          this.getAdminOrder();
        }
      }
    });
  }

  setServiceData(service: any): void {
    this.selectedService = service?.id || service?.pk;
    this.form.patchValue({ service_id: service?.id || service?.pk });
  }

  getDynamicServices(params: any): void {
    let servicesParams = { ...params.params };
    servicesParams['is_active'] = true;
    servicesParams['page_size'] = 20;

    if (params.clearList) {
      this.services = [];
      this.servicesList = [];
      this.setServiceData(null);
    }

    this.dynamicServices.getServices(servicesParams).subscribe({
      next: (data) => {
        this.services.push(...data.results);
        this.servicesList = [...this.services];
        this.servicesPageCount = data.pages_count;
      },
      error: (error) => console.error('Error loading dynamic services:', error),
    });
  }

  setClientData(client: any): void {
    this.selectedClient = client?.id || client?.pk;
    this.form.patchValue({ customer_id: client?.id || client?.pk });
  }

  getDynamicClients(params: any): void {
    let clientsParams = { ...params.params };
    clientsParams['page_size'] = 20;

    if (params.clearList) {
      this.clients = [];
      this.clientsList = [];
      this.setClientData(null);
    }

    this.clientsService.getAllClients(clientsParams).subscribe({
      next: (data) => {
        this.clients.push(...data.results);
        this.clientsList = [...this.clients];
        this.clientsPageCount = data.pages_count;
      },
      error: (error) => console.error('Error loading clients:', error),
    });
  }

  addClient(): void {
    this.clientDialogRef = this.dialog.open(AddClientComponent, {
      disableClose: true,
    });

    this.clientDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getDynamicClients({ params: { page: 1 }, clearList: true });
        this.selectedClient = result?.id || result?.pk;
        this.form.patchValue({ customer_id: result?.id || result?.pk });
      }
    });
  }

  setupFormListeners(): void {
    // Listen for changes in service_id AND customer_id
    this.form.get('service_id')?.valueChanges.subscribe(() => {
      this.checkAndLoadUnits();
    });

    this.form.get('customer_id')?.valueChanges.subscribe(() => {
      this.checkAndLoadUnits();
    });

    // Listen for unit selection to load packages
    this.form.get('property_unit_id')?.valueChanges.subscribe((unitId) => {
      if (unitId) {
        this.loadPackagesForSelectedUnit(unitId);
      } else {
        this.showPackagesDropdown = false;
        this.availablePackages = [];
      }
    });
  }

  checkAndLoadUnits(): void {
    const serviceId = this.form.get('service_id')?.value;
    const customerId = this.form.get('customer_id')?.value;

    // Only reset unit and package selections if we're not loading an existing order
    if (!this.pendingNewUnitId && !this.pendingPackageId) {
      this.form.patchValue(
        {
          property_unit_id: null,
          package_id: null,
        },
        { emitEvent: false },
      );
    }

    if (serviceId && customerId) {
      this.loadCustomerUnits(serviceId, customerId);
    } else {
      // Hide units dropdown if selections are incomplete
      this.showUnitsDropdown = false;
      this.showPackagesDropdown = false;
      this.customerUnits = [];
      this.availablePackages = [];
    }
  }

  loadCustomerUnits(serviceId: number, customerId: number): void {
    this.spinner = true;

    this.propertyUnitsService.loadCustomerUnits(serviceId, customerId).subscribe({
      next: (response) => {
        // Store units with their packages
        this.customerUnits = response.map((unit: any) => ({
          ...unit,
          packages: unit.packages || [] // Keep packages array with each unit
        }));
        this.showUnitsDropdown = true;
        this.spinner = false;
        
        // Auto-select the unit if we have a pending ID
        if (this.pendingNewUnitId) {
          const newUnit = this.customerUnits.find(unit => unit.id === this.pendingNewUnitId);
          if (newUnit) {
            this.form.patchValue({ property_unit_id: this.pendingNewUnitId });
            this.pendingNewUnitId = null; // Clear the pending ID
          }
        }
      },
      error: (error) => {
        this.spinner = false;
        this.showUnitsDropdown = false;
      }
    });
  }

  addNewUnit(): void {
    // Switch to add-unit view instead of navigating
    this.currentView = 'add-unit';
  }

  loadPackagesForSelectedUnit(unitId: number): void {
    // Find the selected unit from customerUnits
    const selectedUnit = this.customerUnits.find(unit => unit.id === unitId);
    
    if (selectedUnit && selectedUnit.packages) {
      
      // Use packages from the unit response
      this.availablePackages = selectedUnit.packages.map((pkg: any) => ({
        id: pkg.id,
        name: pkg.name,
        price: pkg.cost,
        starting_price: pkg.cost,
        description: pkg.description || '',
        package_title: pkg.package_title || '',
        order_type: pkg.order_type || '',
        dynamic_service: pkg.dynamic_service,
        building_type: pkg.building_type,
        package_meters: pkg.package_meters,
        cost_unit: pkg.cost_unit,
        ayen_extra_meter_cost: pkg.ayen_extra_meter_cost,
        best_choices: pkg.best_choices,
        features: pkg.features || []
      }));
      
      this.showPackagesDropdown = true;
      
      // Auto-select package if we have a pending package ID
      if (this.pendingPackageId) {
        const packageExists = this.availablePackages.find(pkg => pkg.id === this.pendingPackageId);
        if (packageExists) {
          this.form.patchValue({ package_id: this.pendingPackageId });
          this.pendingPackageId = null; // Clear after selection
        }
      }
    } else {
      // No packages found for this unit
      this.availablePackages = [];
      this.showPackagesDropdown = true;
    }
  }

  selectUnit(unit: any): void {
    this.form.patchValue({ property_unit_id: unit.id });
  }

  selectPackage(pkg: any): void {
    this.form.patchValue({ package_id: pkg.id });
  }

  save(): void {
    this.alertmsg = false;
    if (this.form.valid) {
      this.spinner = true;

      // Get selected package details to extract order info
      const selectedPackage = this.availablePackages.find(
        pkg => pkg.id === this.form.value.package_id
      );

      // Build order payload
      const orderPayload = {
        order_client: this.form.value.customer_id,
        dynamic_service: this.form.value.service_id,
        property_unit: this.form.value.property_unit_id,
        package: this.form.value.package_id,
        order_type: selectedPackage?.order_type || null,
        building_type: selectedPackage?.building_type || null,
      };
      const orderObservable = this.orderId 
        ? this.adminOrdersService.updateOrder(this.orderId, orderPayload)
        : this.adminOrdersService.createNewOrder(orderPayload);


      orderObservable.subscribe({
        next: (response) => {
          this.spinner = false;
          
          // Only update URL params if this was a new order creation
          if (!this.orderId) {
            const params = { id: response.id };
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: params,
              queryParamsHandling: '',
            });
            this.orderId = response.id;
          }
          
          // Emit the view change with the order ID
          this.building_view.emit({
            view: 'address',
            id: this.orderId || response.id,
            dynamic_service: response.dynamic_service || this.form.value.service_id,
          });
          this.showConfirmationModal();
        },
        error: (error) => {
          console.error('Error saving order:', error);
          this.spinner = false;
          this.alertmsg = true;
          this.errorMessage = 
            Object.values(error.error || {})[0]?.toString() || 
            'حدث خطأ أثناء حفظ الطلب';
        },
      });
    } else {
      this.alertmsg = true;
      this.errorMessage = 'يوجد خطأ في بعض البيانات';
    }
  }

  showConfirmationModal(): void {
    this.errorMsg.emit({
      message: 'تم حفظ البيانات بنجاح.',
      imgSuccess: true,
    });
  }

  hideErrorMSG(): void {
    this.alertmsg = false;
  }
  
  // Handle unit added event from add-property-unit component
  onUnitAdded(event: any): void {
    this.pendingNewUnitId = event.id;
    
    // Return to main view
    this.currentView = 'main';
    
    // Reload customer units to include the new one
    const serviceId = this.form.get('service_id')?.value;
    const customerId = this.form.get('customer_id')?.value;
    
    if (serviceId && customerId) {
      this.loadCustomerUnits(serviceId, customerId);
    }
  }
  
  onAddUnitCancelled(): void {
    this.currentView = 'main';
  }
  
  // Get existing order data
  getAdminOrder(): void {
    this.spinner = true;
    this.adminOrdersService.getAdminOrder(this.orderId).subscribe({
      next: (data) => {
        this.order = data;
        this.patchValues();
        this.spinner = false;
      },
      error: (error) => {
        this.spinner = false;
      }
    });
  }
  
  // Patch form with existing order values
  patchValues(): void {
    if (!this.order) return;
    
    // Set pending IDs FIRST before patching any form values
    // Check if property_unit is an ID (number) or an object with id property
    if (this.order.property_unit) {
      if (typeof this.order.property_unit === 'object' && this.order.property_unit.id) {
        this.pendingNewUnitId = this.order.property_unit.id;
      } else if (typeof this.order.property_unit === 'number') {
        this.pendingNewUnitId = this.order.property_unit;
      }
    }
    
    // Check if package is an ID (number) or an object with id property
    if (this.order.package) {
      if (typeof this.order.package === 'object' && this.order.package.id) {
        this.pendingPackageId = this.order.package.id;
      } else if (typeof this.order.package === 'number') {
        this.pendingPackageId = this.order.package;
      }
    }
    
    // Patch customer (with emitEvent: false to prevent triggering listeners)
    if (this.order.order_details?.order_client_object?.id) {
      const clientObj = {
        id: this.order.order_details.order_client_object.id,
        full_name: this.order.order_details.order_client_object.name,
      };
      this.clients = [];
      this.clients.push(clientObj);
      this.clientsList = [...this.clients];
      this.selectedClient = clientObj.id;
      this.form.patchValue({ customer_id: clientObj.id }, { emitEvent: false });
    }
    
    // Patch service (with emitEvent: false to prevent triggering listeners)
    if (this.order.dynamic_service) {
      const serviceObj = {
        id: this.order.order_details.dynamic_service.id,
        name: this.order.order_details.dynamic_service.name,
      };
      this.services = [];
      this.services.push(serviceObj);
      this.servicesList = [...this.services];
      this.selectedService = serviceObj.id;
      this.form.patchValue({ service_id: serviceObj.id }, { emitEvent: false });
    }
    
    // Load customer units if we have both service and customer
    // This will auto-select the unit and trigger package loading
    if (this.order.dynamic_service && this.order.order_details?.order_client_object?.id) {
      this.loadCustomerUnits(
        this.order.dynamic_service,
        this.order.order_details.order_client_object.id
      );
    }
  }
}
